import { createContext, useContext, useState, useEffect } from "react";
import { useAuth, useToast } from "../hooks";
import { NOTIFICATION_TYPES } from "../utils";

const IntegrationsContext = createContext();

const TEAM_INTEGRATIONS_ENDPOINT = "integration/team-integrations/";

export const useIntegrations = () => {
  const context = useContext(IntegrationsContext);
  if (!context) {
    throw new Error("useIntegrations must be used within IntegrationsProvider");
  }
  return context;
};

export const IntegrationsProvider = ({ children }) => {
  const { apiClient, isLoggedIn } = useAuth();
  const showToast = useToast();

  const [loading, setLoading] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [error, setError] = useState(null);

  // Fetch all integrations for the team
  const fetchAllIntegrations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(TEAM_INTEGRATIONS_ENDPOINT);
      setIntegrations(response.data.results || []);
    } catch (err) {
      console.error("Failed to fetch integrations:", err);
      const errorMsg = "Failed to load integrations. Please try again.";
      setError(errorMsg);
      showToast({
        content: errorMsg,
        type: NOTIFICATION_TYPES.error,
      });
    } finally {
      setLoading(false);
    }
  };

  // Connect a new integration
  const connectIntegration = async (integrationData) => {
    setError(null);
    try {
      const response = await apiClient.post(
        TEAM_INTEGRATIONS_ENDPOINT,
        integrationData,
      );
      await fetchAllIntegrations();
      showToast({
        content: `${integrationData.integration_id} connected successfully!`,
        type: NOTIFICATION_TYPES.success,
      });
      return response.data;
    } catch (err) {
      console.error("Failed to connect integration:", err);
      const errorMsg =
        err.response?.data?.error ||
        "Failed to connect integration. Please try again.";
      setError(errorMsg);
      showToast({
        content: errorMsg,
        type: NOTIFICATION_TYPES.error,
      });
      throw err;
    }
  };

  // Disconnect an integration
  const disconnectIntegration = async (integrationId) => {
    setError(null);
    try {
      const response = await apiClient.delete(
        `${TEAM_INTEGRATIONS_ENDPOINT}${integrationId}/`,
      );

      if (response.status !== 204) {
        throw new Error("Failed to disconnect integration");
      }

      setIntegrations(integrations.filter((i) => i.id !== integrationId));

      showToast({
        content: "Integration disconnected successfully!",
        type: NOTIFICATION_TYPES.success,
      });
    } catch (err) {
      console.error("Failed to disconnect integration:", err);
      const errorMsg =
        err.response?.data?.error ||
        "Failed to disconnect integration. Please try again.";
      setError(errorMsg);
      showToast({
        content: errorMsg,
        type: NOTIFICATION_TYPES.error,
      });
      throw err;
    }
  };

  // Get integration by integration_id
  const getIntegrationById = (integrationId) => {
    return integrations.find((i) => i.integration_id === integrationId);
  };

  // Check if an integration is connected (has active OAuth/API connection)
  const isIntegrationConnected = (integrationId) => {
    const integration = integrations.find(
      (i) => i.integration_id === integrationId,
    );
    if (!integration) return false;
    // Use backend-computed is_connected if available, fall back to existence check
    return integration.is_connected ?? true;
  };

  // Effects
  useEffect(() => {
    if (apiClient && isLoggedIn) {
      fetchAllIntegrations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClient, isLoggedIn]);

  const value = {
    loading,
    integrations,
    error,
    fetchAllIntegrations,
    connectIntegration,
    disconnectIntegration,
    getIntegrationById,
    isIntegrationConnected,
  };

  return (
    <IntegrationsContext.Provider value={value}>
      {children}
    </IntegrationsContext.Provider>
  );
};
